import React, { useState } from 'react';
import Loader from 'components/Loader/Loader';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useSitrepTemplatesV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type SitrepTemplateSelectModalProps = {
  show: boolean;
  onHide: () => void;
  onSelect: (templateId: string) => void;
  isSubmitting?: boolean;
};

const SitrepTemplateSelectModal: React.FC<SitrepTemplateSelectModalProps> = ({
  show,
  onHide,
  onSelect,
  isSubmitting = false,
}) => {
  const { data: templates = [], isLoading, isError } = useSitrepTemplatesV2();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  // Reset the selection whenever the modal closes, regardless of whether it
  // was dismissed or a template was successfully submitted. Adjusted during
  // render (rather than in an effect) to avoid a cascading extra render.
  const [prevShow, setPrevShow] = useState(show);
  if (show !== prevShow) {
    setPrevShow(show);
    if (!show) {
      setSelectedTemplateId(null);
    }
  }

  const effectiveSelectedId = selectedTemplateId ?? templates[0]?.id ?? null;

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent className="tw:max-h-[85vh] tw:overflow-y-auto tw:sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>Create Draft Report</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <div className="text-center">
            <span>Failed to load SITREP templates.</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center">
            <span>No SITREP templates are available.</span>
          </div>
        ) : (
          <div>
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                role="radio"
                aria-checked={template.id === effectiveSelectedId}
                className={`d-flex align-items-center mb-2 w-100 gap-2 rounded border p-3 ${
                  template.id === effectiveSelectedId
                    ? 'border-primary text-primary'
                    : ''
                }`}
                onClick={() => {
                  setSelectedTemplateId(template.id);
                }}
              >
                <SharedIcon
                  iconName={
                    template.id === effectiveSelectedId
                      ? 'radio_button_checked'
                      : 'radio_button_unchecked'
                  }
                  size="sm"
                />
                {template.name}
                <Badge variant="outline">System</Badge>
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => effectiveSelectedId && onSelect(effectiveSelectedId)}
            disabled={!effectiveSelectedId || isSubmitting}
          >
            {isSubmitting ? 'Creating' : 'Create Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SitrepTemplateSelectModal;
